/**
 * Asset Status Color Utilities
 * 
 * Centralized color system for asset-related statuses across the Asset Admin portal.
 * 
 * Color Convention:
 * - Green: Positive/Available/Success (available, active, completed, approved)
 * - Blue: In Use/Assigned/Processing (assigned, in_progress)
 * - Yellow: Pending/Warning (pending, pending_acceptance, in_repair, reserved)
 * - Red: Negative/Problem (rejected, lost, pending_disposal)
 * - Gray: Inactive/Neutral (disposed, deactivated, cancelled)
 */

// ============================================
// ASSET STATUS COLORS
// ============================================
export const assetStatusColors = {
  // Positive - Green
  available: 'bg-green-100 text-green-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-green-100 text-green-800',
  approved: 'bg-green-100 text-green-800',
  
  // In Use - Blue
  assigned: 'bg-blue-100 text-blue-800',
  deployed: 'bg-indigo-100 text-indigo-800',
  in_progress: 'bg-blue-100 text-blue-800',
  
  // Pending/Warning - Yellow
  pending: 'bg-yellow-100 text-yellow-800',
  pending_acceptance: 'bg-yellow-100 text-yellow-800',
  pending_receipt: 'bg-yellow-100 text-yellow-800',
  in_repair: 'bg-yellow-100 text-yellow-800',
  at_workshop: 'bg-yellow-100 text-yellow-800',
  reserved: 'bg-yellow-100 text-yellow-800',
  
  // Negative/Problem - Red
  rejected: 'bg-red-100 text-red-800',
  lost: 'bg-red-100 text-red-800',
  pending_disposal: 'bg-red-100 text-red-800',
  written_off: 'bg-red-100 text-red-800',
  
  // Inactive/Neutral - Gray
  disposed: 'bg-gray-100 text-gray-500',
  terminated: 'bg-gray-100 text-gray-500',
  returned_to_lessor: 'bg-gray-100 text-gray-500',
  deactivated: 'bg-gray-100 text-gray-500',
  cancelled: 'bg-gray-100 text-gray-500',
  scrapped: 'bg-gray-100 text-gray-500',
  sold: 'bg-gray-100 text-gray-500',
};

// ============================================
// ASSET STATUS LABELS
// ============================================
export const assetStatusLabels = {
  // Asset statuses
  available: 'Available',
  assigned: 'Assigned',
  in_repair: 'In Repair',
  at_workshop: 'At Workshop',
  pending_disposal: 'Pending Disposal',
  disposed: 'Disposed',
  terminated: 'Terminated',
  returned_to_lessor: 'Returned to Lessor',
  reserved: 'Reserved',
  lost: 'Lost',
  
  // Fleet-specific disposal statuses
  scrapped: 'Scrapped',
  sold: 'Sold',
  written_off: 'Written Off',
  
  // Deployment statuses
  deployed: 'Deployed',
  
  // Access Control statuses
  active: 'Active',
  deactivated: 'Deactivated',
  
  // Transfer statuses
  pending_acceptance: 'Pending',
  pending_receipt: 'Pending Receipt',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  
  // Checklist/Disposal statuses
  pending: 'Pending',
  in_progress: 'In Progress',
  approved: 'Approved',
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the CSS classes for a status badge
 * @param {string} status - The status value
 * @returns {string} - Tailwind CSS classes
 */
export const getStatusColorClasses = (status) => {
  return assetStatusColors[status] || 'bg-gray-100 text-gray-600';
};

/**
 * Get the display label for a status
 * @param {string} status - The status value
 * @returns {string} - Human-readable label (capitalized)
 */
export const getStatusLabel = (status) => {
  if (assetStatusLabels[status]) {
    return assetStatusLabels[status];
  }
  // Fallback: capitalize first letter and replace underscores with spaces
  if (!status) return '';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Get both color classes and label for a status
 * @param {string} status - The status value
 * @returns {{ classes: string, label: string }}
 */
export const getStatusDisplay = (status) => {
  return {
    classes: getStatusColorClasses(status),
    label: getStatusLabel(status),
  };
};

// ============================================
// CATEGORY COLOR UTILITIES
// ============================================
export const categoryColorClasses = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600' },
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  sky: { bg: 'bg-sky-100', text: 'text-sky-600' },
};

/**
 * Get category color classes by category code
 * @param {string} categoryCode - The category code (IT, ACC, FLT, LIC, OFF, FUEL, DSL)
 * @returns {{ bg: string, text: string }}
 */
export const getCategoryColorByCode = (categoryCode) => {
  const codeToColor = {
    IT: 'blue',      // IT Equipment - Blue
    ACC: 'green',    // Access Control - Green
    FLT: 'orange',   // Fleet - Orange
    LIC: 'purple',   // Software Licenses - Purple
    OFF: 'gray',     // Office Equipment - Gray
    FUEL: 'orange',  // Fuel Cards - Orange
    DSL: 'amber',    // Diesel Tokens - Amber
    TEL: 'sky',      // Mobile Lines - Sky
  };
  const color = codeToColor[categoryCode] || 'gray';
  return categoryColorClasses[color];
};

export default {
  assetStatusColors,
  assetStatusLabels,
  getStatusColorClasses,
  getStatusLabel,
  getStatusDisplay,
  categoryColorClasses,
  getCategoryColorByCode,
};
